import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../store/authStore'
import { useToastStore } from '../../store/toastStore'
import { useFeatureFlags } from '../../hooks/shared/useFeatureFlags'
import { translateApiError } from '../../lib/apiErrors'
import { getRuntimeEnv } from '../../lib/runtime-env'
import AuthBrand from '../../components/auth/AuthBrand'
import AuthShell from '../../components/auth/AuthShell'
import {
  authDividerLabelClass,
  authDividerLineClass,
  authHeadingClass,
  authInputClass,
  authLinkClass,
  authMutedLinkClass,
  authSubtextClass,
  authSubmitClass,
} from '../../components/auth/authTheme'

declare global {
  interface Window {
    google: any
  }
}

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const { t } = useTranslation(['auth', 'common'])
  const { login, googleLogin, isLoading } = useAuthStore()
  const { addToast } = useToastStore()
  const { isRegistrationEnabled, isGoogleSSOEnabled, isLoading: flagsLoading } = useFeatureFlags()
  const navigate = useNavigate()
  const location = useLocation()

  const fromPath = location.state?.from?.pathname || '/'
  const fromSearch = location.state?.from?.search || ''
  const from = `${fromPath}${fromSearch}`

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      await login(email, password)
      addToast(t('auth:login.success'), 'success')
      navigate(from, { replace: true })
    } catch (error) {
      addToast(translateApiError(error, t), 'error')

      const code = (error as any)?.response?.data?.code
      if (code === 'auth.email_not_verified') {
        setTimeout(() => {
          addToast(
            <div>
              {t('auth:login.verificationNeeded')}{' '}
              <Link
                to="/auth/resend-verification"
                className="font-medium underline text-white hover:text-gray-200"
              >
                {t('auth:login.verificationNeededLink')}
              </Link>
            </div>,
            'info',
            7000
          )
        }, 1000)
      }
    }
  }

  const handleGoogleLogin = async (credentialResponse: any) => {
    try {
      await googleLogin(credentialResponse.credential)
      addToast(t('auth:login.googleSuccess'), 'success')
      navigate(from, { replace: true })
    } catch (error) {
      addToast(translateApiError(error, t), 'error')
    }
  }

  useEffect(() => {
    if (flagsLoading) return
    if (!isGoogleSSOEnabled()) return

    const initializeGoogleSignIn = () => {
      const clientId = getRuntimeEnv('VITE_GOOGLE_CLIENT_ID')

      if (!clientId) {
        console.error('VITE_GOOGLE_CLIENT_ID is not set!')
        addToast(t('auth:login.googleConfigError'), 'error')
        return
      }

      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleLogin,
          auto_select: false,
          cancel_on_tap_outside: true,
        })

        window.google.accounts.id.renderButton(
          document.getElementById('google-signin-button'),
          {
            theme: 'outline',
            size: 'large',
            width: '384',
            text: 'signin_with',
          }
        )
      }
    }

    if (!window.google) {
      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client'
      script.onload = initializeGoogleSignIn
      document.body.appendChild(script)
    } else {
      initializeGoogleSignIn()
    }
  }, [flagsLoading, isGoogleSSOEnabled])

  return (
    <AuthShell>
      <div>
        <AuthBrand />
        <h2 className={authHeadingClass}>{t('auth:login.heading')}</h2>
        {!flagsLoading && isRegistrationEnabled() && (
          <p className={authSubtextClass}>
            {t('auth:login.orPrompt')}{' '}
            <Link to="/auth/register" className={authLinkClass}>
              {t('auth:login.createAccountLink')}
            </Link>
          </p>
        )}
      </div>

      <form className="mt-8 space-y-6" onSubmit={handleEmailLogin}>
        <div className="rounded-md shadow-sm -space-y-px">
          <div>
            <label htmlFor="email" className="sr-only">
              {t('common:form.emailLabel')}
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              className={`${authInputClass} appearance-none rounded-none rounded-t-md focus:z-10`}
              placeholder={t('common:form.emailLabel')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <div className="relative">
            <label htmlFor="password" className="sr-only">
              {t('common:form.passwordLabel')}
            </label>
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              className={`${authInputClass} appearance-none rounded-none rounded-b-md pr-10 focus:z-10`}
              placeholder={t('common:form.passwordLabel')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <Link to="/auth/forgot-password" className={authMutedLinkClass}>
            {t('auth:login.forgotPassword')}
          </Link>
        </div>

        <div>
          <button type="submit" disabled={isLoading} className={authSubmitClass}>
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#0d0d0d]" />
            ) : (
              t('auth:login.submitButton')
            )}
          </button>
        </div>

        {isGoogleSSOEnabled() && (
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className={authDividerLineClass} />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className={authDividerLabelClass}>{t('auth:login.orLoginWith')}</span>
              </div>
            </div>

            <div className="mt-6">
              <div id="google-signin-button" className="w-full flex justify-center" />
              <p className="mt-2 text-xs text-gray-500 text-center">{t('auth:login.googleOnly')}</p>
            </div>
          </div>
        )}
      </form>
    </AuthShell>
  )
}
